import { inject, Injectable } from '@angular/core';
import { ToastController } from '@ionic/angular';

export type ToastType = 'info' | 'success' | 'warning' | 'error';
export type ToastMessage = string | string[];

/**
 * Replaces AlertService and CoAlert.
 *
 * Every message used to be a modal `ion-alert` with a "Tutup" button, and
 * callers hung their follow-up work off its callback -- so a successful save
 * only navigated once the user dismissed the dialog, and a backdrop tap threw
 * that callback away entirely. A toast cannot gate anything, so the callback
 * mechanism is gone with it: call sites now act immediately and let the toast
 * report what happened.
 *
 * No global mount, unlike CoLoader: ToastController registers `ion-toast`
 * itself and presents into the overlay portal at the ion-app root, which is
 * also why the toast survives the navigation that usually follows it.
 */
const toastColors: Record<ToastType, string> = {
  info: 'medium',
  success: 'success',
  warning: 'warning',
  error: 'danger',
};

/**
 * Failures need reading time; a confirmation is glanceable.
 *
 * `warning` no longer collapses into `info` the way AlertService's did. That
 * mapping existed because there was no warning styling to map onto -- Ionic
 * ships a warning colour, so the reason is gone.
 */
const toastDurations: Record<ToastType, number> = {
  info: 2500,
  success: 2500,
  warning: 3500,
  error: 4000,
};

@Injectable({ providedIn: 'root' })
export class ToastService {
  private readonly toasts = inject(ToastController);

  /** The toast on screen, so a burst of messages replaces rather than stacks. */
  private current: HTMLIonToastElement | null = null;

  info(message: ToastMessage): void {
    this.show('info', message);
  }

  success(message: ToastMessage): void {
    this.show('success', message);
  }

  warning(message: ToastMessage): void {
    this.show('warning', message);
  }

  error(message: ToastMessage): void {
    this.show('error', message);
  }

  /** Presenting is fire-and-forget, so a toast never delays a navigation. */
  private show(type: ToastType, message: ToastMessage): void {
    void this.present(type, message);
  }

  private async present(type: ToastType, message: ToastMessage): Promise<void> {
    const text = Array.isArray(message) ? message.join('\n') : message;

    /* The API sends '' on some failures; an empty toast is just a floating bar. */
    if (!text) {
      return;
    }

    await this.dismissCurrent();

    const toast = await this.toasts.create({
      message: text,
      color: toastColors[type],
      duration: toastDurations[type],
      position: 'bottom',
      cssClass: `co-toast co-toast-${type}`,
    });

    this.current = toast;
    void toast.onDidDismiss().then(() => {
      /* Only clear if nothing has replaced it in the meantime. */
      if (this.current === toast) {
        this.current = null;
      }
    });

    await toast.present();
  }

  /** For a caller that wants the screen clear before it does something else. */
  async dismiss(): Promise<void> {
    await this.dismissCurrent();
  }

  private async dismissCurrent(): Promise<void> {
    const toast = this.current;
    this.current = null;

    /* Already gone if it auto-dismissed between create and here. */
    await toast?.dismiss().catch(() => undefined);
  }
}
